import { HttpErrorResponse } from "@angular/common/http";

import { BankBatch } from "@core/models/bank-batch-model";
import { BankCollectionBatch } from "@core/models/bank-collection-batch";
import { CollectionTransaction } from "@core/models/collection-transaction.model";
import { ExtendedGfhAccount } from "@core/models/extended-gfh-account.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { PendingTransaction } from "@core/models/pending-transaction.model";
import { Search } from "@core/models/search.model";
import { Transaction } from "@core/models/transaction.model";
import { createReducer, on } from "@ngrx/store";

import * as TransactionActions from "../store/transaction.actions";

export const TRANSACTION_FEATURE_KEY = "transaction";

export interface TransactionState {
  bankBatches: Array<BankBatch>;
  transactions: Array<Transaction>;
  funds: Array<FundWithRelated>;
  gfhAccounts: Array<ExtendedGfhAccount>;
  bankCollectionBatches: Array<BankCollectionBatch>;
  bankRetryCollectionBatches: Array<BankCollectionBatch>;
  bankFailedCollectionBatches: Array<CollectionTransaction>;
  disbursementList: Array<PendingTransaction>;
  searchItems: Search | null;
  loading: boolean;
  collectionLoading: boolean;
  retryCollectionLoading: boolean;
  failedCollectionLoading: boolean;
  disbursementLoading: boolean;
  selectedDisbursement: boolean;
  error: HttpErrorResponse | null;
}

const initialState: TransactionState = {
  bankBatches: [],
  transactions: [],
  funds: [],
  gfhAccounts: [],
  bankCollectionBatches: [],
  bankRetryCollectionBatches: [],
  bankFailedCollectionBatches: [],
  disbursementList: [],
  searchItems: null,
  loading: false,
  collectionLoading: false,
  retryCollectionLoading: false,
  failedCollectionLoading: false,
  disbursementLoading: false,
  selectedDisbursement: false,
  error: null,
};

export const TRANSACTION_REDUCER = createReducer(
  initialState,
  on(
    TransactionActions.SET_BANK_BATCHES,
    (
      state: TransactionState,
      action: { bankBatchList: Array<BankBatch> }
    ): TransactionState => {
      return {
        ...state,
        bankBatches: action.bankBatchList,
      };
    }
  ),
  on(
    TransactionActions.SET_FUNDS,
    (
      state: TransactionState,
      action: { fundList: Array<FundWithRelated> }
    ): TransactionState => {
      return {
        ...state,
        funds: action.fundList,
      };
    }
  ),
  on(
    TransactionActions.SET_TRANSACTIONS,
    (
      state: TransactionState,
      action: { transactionList: Array<Transaction> }
    ): TransactionState => {
      return {
        ...state,
        transactions: action.transactionList,
      };
    }
  ),
  on(
    TransactionActions.SET_TRANSACTION_LOADING_STATUS,
    (
      state: TransactionState,
      action: { loading: boolean }
    ): TransactionState => {
      return {
        ...state,
        loading: action.loading,
      };
    }
  ),
  on(
    TransactionActions.SET_TRANSACTION_ERROR,
    (
      state: TransactionState,
      action: { error: HttpErrorResponse }
    ): TransactionState => {
      return {
        ...state,
        error: action.error,
      };
    }
  ),
  on(
    TransactionActions.SET_GFH_ACCOUNTS,
    (
      state: TransactionState,
      action: { gfhAccountList: Array<ExtendedGfhAccount> }
    ): TransactionState => {
      return {
        ...state,
        gfhAccounts: action.gfhAccountList,
      };
    }
  ),
  on(
    TransactionActions.RESET_GFH_ACCOUNTS,
    (state: TransactionState): TransactionState => {
      return {
        ...state,
        gfhAccounts: [],
      };
    }
  ),
  on(
    TransactionActions.SET_TRANSACTION_SEARCH_ITEMS,
    (
      state: TransactionState,
      action: { searchItems: Search | null }
    ): TransactionState => {
      return {
        ...state,
        searchItems: action.searchItems,
      };
    }
  ),
  on(
    TransactionActions.SET_BANK_COLLECTION_BATCHES,
    (
      state: TransactionState,
      action: { bankCollectionBatchList: Array<BankCollectionBatch> }
    ): TransactionState => {
      return {
        ...state,
        bankCollectionBatches: action.bankCollectionBatchList,
      };
    }
  ),
  on(
    TransactionActions.SET_BANK_RETRY_COLLECTION_BATCHES,
    (
      state: TransactionState,
      action: { bankCollectionBatchList: Array<BankCollectionBatch> }
    ): TransactionState => {
      return {
        ...state,
        bankRetryCollectionBatches: action.bankCollectionBatchList,
      };
    }
  ),
  on(
    TransactionActions.SET_BANK_FAILED_COLLECTION_BATCHES,
    (
      state: TransactionState,
      action: { bankCollectionBatchList: Array<CollectionTransaction> }
    ): TransactionState => {
      return {
        ...state,
        bankFailedCollectionBatches: action.bankCollectionBatchList,
      };
    }
  ),
  on(
    TransactionActions.SET_COLLECTION_LOADING_STATUS,
    (
      state: TransactionState,
      action: { loading: boolean }
    ): TransactionState => {
      return {
        ...state,
        collectionLoading: action.loading,
      };
    }
  ),
  on(
    TransactionActions.SET_RETRY_COLLECTION_LOADING_STATUS,
    (
      state: TransactionState,
      action: { loading: boolean }
    ): TransactionState => {
      return {
        ...state,
        retryCollectionLoading: action.loading,
      };
    }
  ),
  on(
    TransactionActions.SET_FAILED_COLLECTION_LOADING_STATUS,
    (
      state: TransactionState,
      action: { loading: boolean }
    ): TransactionState => {
      return {
        ...state,
        failedCollectionLoading: action.loading,
      };
    }
  ),
  on(
    TransactionActions.SET_DISBURSEMENT_LOADING_STATUS,
    (
      state: TransactionState,
      action: { loading: boolean }
    ): TransactionState => {
      return {
        ...state,
        disbursementLoading: action.loading,
      };
    }
  ),
  on(
    TransactionActions.SET_DISBURSEMENTS,
    (
      state: TransactionState,
      action: { disbursementList: Array<PendingTransaction> }
    ): TransactionState => {
      return {
        ...state,
        disbursementList: action.disbursementList,
      };
    }
  ),
  on(
    TransactionActions.SET_SELECTED_DISBURSEMENT,
    (
      state: TransactionState,
      action: { selectedDisbursement: boolean }
    ): TransactionState => {
      return {
        ...state,
        selectedDisbursement: action.selectedDisbursement,
      };
    }
  )
);
